import { api } from "api";
import {
    getAllUsers,
    deleteUser,
    updateUser,
    addAdmin,
    addDeliveryUser,
    toggleBlockUser as toggleBlockUserUrl,
    adminChangeUserPassword as adminChangeUserPasswordUrl,
    updateUserNote as updateUserNoteUrl,
} from "api/apiURLs";

export const getUsers = async (params) => {
    const response = await api.get(getAllUsers, { params });
    return response.data;
};

export const getUsersDropdown = async (params = {}) => {
    const response = await api.get("user/dropdown", { params });
    return response.data;
};

export const createAdmin = async (data) => {
    const response = await api.post(addAdmin, data);
    return response.data;
};

export const createDelivery = async (data) => {
    const response = await api.post(addDeliveryUser, data);
    return response.data;
};

export const editUser = async (data) => {
    const response = await api.patch(updateUser, data);
    return response.data;
};

export const updateDeliveryShippingPercentage = async (userId, shippingPercentage) => {
    const response = await api.patch(`delivery/${userId}/shipping-percentage`, {
        shipping_percentage: shippingPercentage,
    });

    return response.data;
};

/** Save or clear one driver's order ceiling. */
export const updateDeliveryOrderLimit = async (userId, maxConcurrentOrders) => {
    const response = await api.patch("user/update", {
        id: userId,
        max_concurrent_orders: maxConcurrentOrders,
    });

    return response.data;
};

/**
 * Put one driver on the wallet, or take him off it (api#137).
 *
 * A real boolean, not the string a form would post: this goes as JSON, and the
 * API rejects anything that is not a plain yes or no.
 */
export const updateDeliveryWalletMode = async (userId, walletEnabled) => {
    const response = await api.patch("user/update", {
        id: userId,
        wallet_enabled: walletEnabled,
    });

    return response.data;
};

/** Read the selected driver's rolling performance summary. */
export const getDriverPerformance = async (userId, params = {}) => {
    const response = await api.get(`delivery/${userId}/performance`, { params });
    return response.data;
};

export const addAreasService = async (data) => {
    const response = await api.post("delivery/add-areas", data);
    return response.data;
};
/**
 * Removes a user of any role.
 *
 * Not a plain delete: the API counts what the account owns first, because
 * deleting one was never a row delete. An account with orders, statements,
 * videos or reviews is archived instead — hidden and logged out, with its record
 * intact — and only an account with nothing attached is really destroyed.
 *
 * Resolves for BOTH outcomes. Read `deleted` to tell them apart and `message`
 * for the Arabic explanation, which names the role and the actual counts.
 *
 * @param {number|string} id - The user id.
 * @returns {Promise<{deleted: boolean, message: string, history: Record<string, number>}>}
 */
export const removeUser = async (id) => {
    const response = await api.delete(`${deleteUser}/${id}`);
    return response.data;
};

/** Toggle isBlocked status for a user by ID. */
export const toggleBlockUserService = async (id) => {
    const response = await api.patch(`${toggleBlockUserUrl}/${id}`);
    return response.data;
};

/** Set or clear the admin-authored internal note on a user. Empty string clears it. */
export const updateUserNoteService = async (id, note) => {
    const response = await api.patch(`${updateUserNoteUrl}/${id}`, { note });
    return response.data;
};

/** Admin sets a new password for the given user (no old-password check). */
export const adminChangeUserPasswordService = async (id, password, confirm_password) => {
    const response = await api.patch(`${adminChangeUserPasswordUrl}/${id}`, {
        password,
        confirm_password,
    });
    return response.data;
};

/**
 * Re-verify the caller's own password without rotating their token.
 * Used by the admin Statements pages to gate access — see
 * components/Statements/ElevationGate.
 *
 * Resolves on 200, throws on 401 / network error.
 */
export const verifyAdminPassword = async (password) => {
    const response = await api.post("admin/verify-password", { password });
    return response.data;
};

// --- Driver wallet -------------------------------------------------------

/**
 * A driver's balance and his ledger.
 *
 * The balance comes from the ledger on the server, not from the cached column,
 * so this screen is the one to open when the cached figure looks wrong.
 */
export const getDriverWallet = async (userId, params = {}) => {
    const response = await api.get(`delivery/${userId}/wallet`, { params });
    return response.data;
};

/**
 * Credit a driver. Positive amounts only — the server refuses the rest. Send
 * the SAME `idempotencyKey` when you retry the same top-up, and the server
 * credits it once.
 */
export const topUpDriverWallet = async (userId, { amount, note, idempotencyKey }) => {
    const response = await api.post(`delivery/${userId}/wallet/topup`, {
        amount,
        note,
        idempotencyKey,
    });
    return response.data;
};

/**
 * Take money from a driver's wallet (api#147). `amount` is positive; the
 * server writes it as a negative adjustment. `note` is required: the driver
 * reads it. The balance may go below ₪0.
 */
export const deductDriverWallet = async (userId, { amount, note, idempotencyKey }) => {
    const response = await api.post(`delivery/${userId}/wallet/deduct`, {
        amount,
        note,
        idempotencyKey,
    });
    return response.data;
};
